import { useState, useEffect, useCallback, useRef } from 'react';
import { customerDataUploadApiService } from 'src/api/customerDataUploadApiService';
import {
  UploadedDataResponse,
  UploadedDailyData,
  UploadedWeeklyData,
  UploadedMonthlyData,
  UploadedOfflineData,
  UploadedKpiData,
} from 'src/types';
import { captureException } from 'src/libs/sentry';

interface UseUploadedDataParams {
  customerId: string;
  fileIds?: string[];
  fileId?: string;
  dataType?: string;
  from?: string;
  to?: string;
  enabled?: boolean;
}

interface UseUploadedDataReturn {
  data: UploadedDataResponse['data'] | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useUploadedData = ({
  customerId,
  fileIds,
  fileId,
  dataType = 'all',
  from,
  to,
  enabled = true,
}: UseUploadedDataParams): UseUploadedDataReturn => {
  const [data, setData] = useState<UploadedDataResponse['data'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to store the latest params to avoid dependency issues
  const paramsRef = useRef({
    customerId,
    fileIds,
    fileId,
    dataType,
    from,
    to,
    enabled,
  });

  // Update ref whenever params change
  useEffect(() => {
    paramsRef.current = {
      customerId,
      fileIds,
      fileId,
      dataType,
      from,
      to,
      enabled,
    };
  }, [customerId, fileIds, fileId, dataType, from, to, enabled]);

  const fetchData = useCallback(async () => {
    const {
      customerId: currentCustomerId,
      fileIds: currentFileIds,
      fileId: currentFileId,
      dataType: currentDataType,
      from: currentFrom,
      to: currentTo,
      enabled: currentEnabled,
    } = paramsRef.current;

    // Debug: Check browser console for API call info

    if (!currentEnabled || !currentCustomerId) return;

    setLoading(true);
    setError(null);

    try {
      if (currentFileIds && currentFileIds.length > 0) {
        // Fetch data for all file IDs and merge the results
        const responses = await Promise.all(
          currentFileIds.map((currentFileIdItem) =>
            customerDataUploadApiService.getUploadedData(
              currentCustomerId,
              currentFileIdItem,
              currentDataType,
              currentFrom,
              currentTo,
            ),
          ),
        );

        // Debug: Check browser console for API response info

        if (responses.length === 0) setData(null);
        else {
          // If only one response, use it directly to avoid unnecessary merging
          if (responses.length === 1) {
            // Debug: Check browser console for single response data
            setData(responses[0].data);
          } else {
            // Merge data from multiple responses, avoiding duplicates
            const merged = responses.reduce((acc, curr) => {
              // Helper function to merge arrays without duplicates based on unique identifiers
              const mergeUniqueData = <T extends Record<string, unknown>>(
                accArray: T[],
                currArray: T[],
                uniqueFields: (keyof T)[],
              ): T[] => {
                return accArray.concat(
                  currArray.filter(
                    (currItem) =>
                      !accArray.some((accItem) =>
                        uniqueFields.every(
                          (field) => accItem[field] === currItem[field],
                        ),
                      ),
                  ),
                );
              };

              return {
                fileInfo: curr.data.fileInfo,
                dailyData: mergeUniqueData(
                  (acc.dailyData || []) as unknown as Record<string, unknown>[],
                  (curr.data.dailyData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  ['date', 'campaignId', 'fileId'],
                ) as unknown as UploadedDailyData[],
                weeklyData: mergeUniqueData(
                  (acc.weeklyData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  (curr.data.weeklyData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  ['weekStartDate', 'campaignId', 'fileId'],
                ) as unknown as UploadedWeeklyData[],
                monthlyData: mergeUniqueData(
                  (acc.monthlyData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  (curr.data.monthlyData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  ['monthStartDate', 'campaignId', 'fileId'],
                ) as unknown as UploadedMonthlyData[],
                offlineData: mergeUniqueData(
                  (acc.offlineData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  (curr.data.offlineData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  ['periodStartDate', 'campaignId', 'fileId'],
                ) as unknown as UploadedOfflineData[],
                kpiData: mergeUniqueData(
                  (acc.kpiData || []) as unknown as Record<string, unknown>[],
                  (curr.data.kpiData || []) as unknown as Record<
                    string,
                    unknown
                  >[],
                  ['date', 'fileId'],
                ) as unknown as UploadedKpiData[],
                summary: curr.data.summary,
              };
            }, responses[0]?.data);

            setData(merged);
          }
        }
      } else if (currentFileId) {
        // Fallback to single fileId if provided
        const response = await customerDataUploadApiService.getUploadedData(
          currentCustomerId,
          currentFileId,
          currentDataType,
          currentFrom,
          currentTo,
        );
        setData(response.data);
      } else setData(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch uploaded data';
      setError(errorMessage);
      captureException({ error: err, fn: 'useUploadedData' });
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array since we use ref

  useEffect(() => {
    fetchData();
  }, [customerId, fileId, fileIds, dataType, from, to, enabled, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
};
